import {getIssueFieldBadges, type IssueFieldBadge, type IssueFieldValue} from '@github-ui/issue-field-badges'
import {graphql, useFragment} from 'react-relay'

import type {IssueFieldPills$data, IssueFieldPills$key} from './__generated__/IssueFieldPills.graphql'

type IssueFieldValueNode = NonNullable<NonNullable<IssueFieldPills$data['issueFieldValues']>['nodes']>[number]

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const issueFieldPillsFragment = graphql`
  fragment IssueFieldPills on Issue @argumentDefinitions(showIssueFieldPills: {type: "Boolean!", defaultValue: false}) {
    issueFieldValues(first: 25) @include(if: $showIssueFieldPills) {
      nodes {
        ... on IssueFieldTextValue {
          __typename
          value
          field {
            ... on IssueFieldText {
              name
            }
          }
        }
        ... on IssueFieldSingleSelectValue {
          __typename
          value
          field {
            ... on IssueFieldSingleSelect {
              name
            }
          }
        }
        ... on IssueFieldMultiSelectValue {
          __typename
          options {
            name
          }
          field {
            ... on IssueFieldMultiSelect {
              name
            }
          }
        }
        ... on IssueFieldDateValue {
          __typename
          value
          field {
            ... on IssueFieldDate {
              name
            }
          }
        }
        ... on IssueFieldNumberValue {
          __typename
          numberValue: value
          field {
            ... on IssueFieldNumber {
              name
            }
          }
        }
      }
    }
  }
`

function getIssueFieldBadgesFromNode(fieldValue: IssueFieldValueNode): IssueFieldBadge[] {
  const normalized = normalizeFieldValueNode(fieldValue)
  return normalized ? getIssueFieldBadges(normalized) : []
}

// Maps a Relay `issueFieldValues` node into the framework-agnostic
// `IssueFieldValue` shape shared with the REST dashboard row, so both surfaces
// format pills through the same `getIssueFieldBadges` helper.
function normalizeFieldValueNode(fieldValue: IssueFieldValueNode): IssueFieldValue | null {
  if (!fieldValue || fieldValue.__typename === '%other') return null
  const fieldName = fieldValue.field?.name
  if (!fieldName) return null

  switch (fieldValue.__typename) {
    case 'IssueFieldNumberValue':
      return {fieldName, dataType: 'number', numberValue: fieldValue.numberValue}
    case 'IssueFieldSingleSelectValue':
      return {fieldName, dataType: 'single_select', value: fieldValue.value}
    case 'IssueFieldMultiSelectValue':
      return {
        fieldName,
        dataType: 'multi_select',
        options: (fieldValue.options ?? []).flatMap(option => (option?.name ? [option.name] : [])),
      }
    case 'IssueFieldTextValue':
      return {fieldName, dataType: 'text', value: fieldValue.value}
    case 'IssueFieldDateValue':
      return {fieldName, dataType: 'date', value: fieldValue.value}
    default:
      return null
  }
}

// Safe to call with a null key (returns []), so callers can invoke it
// unconditionally and satisfy the rules of hooks.
export function useIssueFieldBadges(dataKey: IssueFieldPills$key | null): IssueFieldBadge[] {
  const data = useFragment(issueFieldPillsFragment, dataKey)
  const nodes = data?.issueFieldValues?.nodes ?? []
  return nodes.flatMap(fieldValue => getIssueFieldBadgesFromNode(fieldValue))
}
